import React, { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Stack,
    Button,
    AppBar,
    Toolbar,
    Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import logo_cinza from '../../assets/logo_branca.png';

/**
 * Casca das paginas de politica.
 *
 * Repete de proposito o visual de /termos: para o usuario e tudo o mesmo
 * conjunto de documentos, e navegacao nova so atrapalharia. O texto vem dos
 * modulos em `content/`, que sao transcricao literal dos PDFs aprovados --
 * esta tela apenas os apresenta.
 *
 * Sem `blocos`, serve de casca para qualquer conteudo passado como children --
 * e o caso da central em index.jsx.
 *
 * Cada bloco do conteudo e de um destes tipos:
 *   secao -> titulo de secao, em caixa alta no documento
 *   item  -> paragrafo numerado, com a numeracao original em negrito
 *   texto -> paragrafo corrido, sem numeracao
 */
const PolicyPage = ({ titulo, versao, blocos = [], children }) => {
    const navigate = useNavigate();

    // Aba aberta pelo aceite de politicas (target="_blank") nao tem para onde
    // voltar: e a primeira entrada do proprio historico dela, e o botao so
    // ficaria ali sem fazer nada. Lido uma vez, no primeiro render, porque
    // navegar dentro da aba muda o numero e nao muda o fato.
    const [temParaOndeVoltar] = useState(() => window.history.length > 1);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
            {/* Header */}
            <AppBar position="static" elevation={2} sx={{ backgroundColor: '#006a61' }}>
                <Container maxWidth="lg">
                    <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 72 } }}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            gap={1}
                            sx={{ flexGrow: 1, cursor: 'pointer' }}
                            onClick={() => navigate('/')}
                        >
                            <Box
                                component="img"
                                src={logo_cinza}
                                alt="Empiria"
                                sx={{ width: 35, height: 35, objectFit: 'contain' }}
                            />
                            <Typography variant="h6" sx={{ color: 'white' }}>
                                Empiria
                            </Typography>
                        </Stack>
                        {temParaOndeVoltar && (
                            <Button
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate(-1)}
                                sx={{ color: 'white', textTransform: 'none' }}
                            >
                                Voltar
                            </Button>
                        )}
                    </Toolbar>
                </Container>
            </AppBar>

            {/* Conteudo */}
            <Container maxWidth="md" sx={{ py: 3 }}>
                <Paper elevation={3} sx={{ p: { xs: 3, md: 5 }, borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight="bold" color="#006a61" gutterBottom textAlign="center">
                        {titulo}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" mb={4} textAlign="center">
                        {versao}
                    </Typography>

                    <Divider sx={{ mb: 4 }} />

                    {blocos.map((bloco, indice) =>
                        bloco.tipo === 'secao' ? (
                            <Typography
                                key={indice}
                                variant="h6"
                                fontWeight="bold"
                                color="#006a61"
                                sx={{ mt: 4, mb: 1 }}
                            >
                                {bloco.texto}
                            </Typography>
                        ) : (
                            <Typography
                                key={indice}
                                variant="body1"
                                color="text.secondary"
                                paragraph
                                sx={{
                                    textAlign: 'justify',
                                    lineHeight: 1.8,
                                    pl: bloco.tipo === 'item' ? 2 : 0,
                                }}
                            >
                                {bloco.id && <strong>{bloco.id}</strong>} {bloco.texto}
                            </Typography>
                        ),
                    )}

                    {children}

                    {/* Botao de voltar */}
                    {temParaOndeVoltar && (
                        <Stack direction="row" justifyContent="center" mt={4}>
                            <Button
                                variant="contained"
                                onClick={() => navigate(-1)}
                                sx={{
                                    bgcolor: '#006a61',
                                    '&:hover': { bgcolor: '#1E7B54' },
                                    px: 4,
                                    py: 1.5,
                                    textTransform: 'none',
                                }}
                            >
                                Voltar
                            </Button>
                        </Stack>
                    )}
                </Paper>
            </Container>

            {/* Footer */}
            <Box sx={{ py: 3, textAlign: 'center', bgcolor: 'white' }}>
                <Typography variant="body2" color="text.secondary">
                    © Todos os direitos reservados a Empiria
                </Typography>
            </Box>
        </Box>
    );
};

export default PolicyPage;
